/**
 * 行为遥测（plan v2 §2）。匿名、不采 PII，玩家侧不展示任何统计。
 *
 * 可靠性模型（断网场景）：所有事件先进 localStorage 队列、投递成功才出队；
 * 重发时机 = ① online 事件 ② 30s 定时 ③ 下次页面加载 ④ pagehide 时对残留队列
 * 补一发 sendBeacon。服务端 UNIQUE 约束幂等，重发安全。
 *
 * 计时口径（与校准管线一致）：
 *   time_seconds        前台累计（250ms 心跳，页面隐藏不计时）
 *   pause_count         切后台次数
 *   long_idle_seconds   前台但无操作、单次 >120s 的间隔之和（人离开的泄漏，分析侧扣除）
 *   max_idle            最大无操作间隔（卡住时长的行为代理）
 *   wrong_count         放置错误（红叉）次数；hint_count 提示次数
 */

const KEY_PLAYER = 'sd_player';
const KEY_QUEUE = 'sd_evq';
const KEY_ATTEMPTS = 'sd_attempts';

const LONG_IDLE_MS = 120_000;
const TICK_MS = 250;
const FLUSH_INTERVAL_MS = 30_000;
const QUEUE_MAX = 200; // 防隐私模式外的极端积压撑爆配额

// ── 玩家身份 ──

function uuid() {
    if (crypto.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    return [...b].map(x => x.toString(16).padStart(2, '0')).join('');
}

/** 匿名玩家 ID：首次访问生成，存 localStorage。 */
export function playerId() {
    try {
        let id = localStorage.getItem(KEY_PLAYER);
        if (!id) {
            id = uuid();
            localStorage.setItem(KEY_PLAYER, id);
        }
        return id;
    } catch {
        return 'anon'; // 隐私模式：仍上报但无法跨访问关联
    }
}

/** mobile / desktop（粗粒度即可）。 */
export function deviceKind() {
    return matchMedia?.('(pointer: coarse)')?.matches ? 'mobile' : 'desktop';
}

// ── 事件队列：先落地，投递成功才出队 ──

function readQueue() {
    try {
        const q = JSON.parse(localStorage.getItem(KEY_QUEUE) || '[]');
        return Array.isArray(q) ? q : [];
    } catch {
        return [];
    }
}

function writeQueue(q) {
    try {
        localStorage.setItem(KEY_QUEUE, JSON.stringify(q.slice(-QUEUE_MAX)));
    } catch { /* 配额满/隐私模式：这批只活在内存里 */ }
}

let memQueue = null; // localStorage 不可用时的会话内兜底
let flushing = false;

function queue() {
    return memQueue ?? readQueue();
}

function saveQueue(q) {
    if (memQueue) { memQueue = q; return; }
    writeQueue(q);
}

/**
 * 入队并立即尝试冲刷。
 * @param {string} url @param {object} body
 */
export function send(url, body) {
    let q = queue();
    q.push({ url, body });
    if (memQueue == null) {
        writeQueue(q);
        // 写入失败探测：读回长度不符则转内存队列
        if (readQueue().length !== q.length) memQueue = q;
    } else {
        memQueue = q;
    }
    flush();
}

/** 把队列里的事件按序投递；失败即停（保持顺序，等下次时机）。 */
export async function flush() {
    if (flushing || !navigator.onLine) return;
    flushing = true;
    try {
        let q = queue();
        while (q.length) {
            const { url, body } = q[0];
            try {
                const res = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                    keepalive: true,
                });
                // 4xx = 服务端判定脏数据，重发也不会好，丢弃防卡队列；5xx/网络错误保留重试
                if (!res.ok && res.status < 500) { /* drop */ }
                else if (!res.ok) break;
            } catch {
                break;
            }
            q = q.slice(1);
            saveQueue(q);
        }
    } finally {
        flushing = false;
    }
}

/** 页面关闭前对残留队列补发 beacon（不出队 —— 无回执，靠服务端幂等去重）。 */
function beaconRemainder() {
    if (!navigator.sendBeacon) return;
    for (const { url, body } of queue().slice(0, 20)) {
        navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }));
    }
}

window.addEventListener('online', flush);
window.addEventListener('pagehide', beaconRemainder);
setInterval(flush, FLUSH_INTERVAL_MS);
flush(); // 页面加载即补发上次残留

// ── attempt_no：同日同 slot 递增，跨天作废 ──

/** @param {string} date @param {number} slot @returns {number} 本次是第几次尝试 */
function nextAttemptNo(date, slot) {
    let rec;
    try {
        rec = JSON.parse(localStorage.getItem(KEY_ATTEMPTS) || 'null');
    } catch { /* fallthrough */ }
    if (!rec || rec.date !== date) rec = { date, counts: {} };
    rec.counts[slot] = (rec.counts[slot] || 0) + 1;
    try {
        localStorage.setItem(KEY_ATTEMPTS, JSON.stringify(rec));
    } catch { /* 忽略 */ }
    return rec.counts[slot];
}

// ── 开题事件 ──

/**
 * 开题（漏斗分母）。同 (player,date,slot) 服务端幂等，重开不产生重复。
 * @param {string} date 题单日期 @param {number} slot 1-20
 */
export function sendStart(date, slot) {
    send('/api/start', { player_id: playerId(), date, slot });
}

// ── 单次尝试的计量器 ──

export class AttemptTracker {
    /**
     * 不收 sbn：上报只带 (date, slot)，服务端自己按题单回填题面。
     * 题面不出网，也就不会从上行请求里漏出去。
     * @param {string} date 题单日期（服务端下发，非本地时区日期）
     * @param {number} slot 1-20
     */
    constructor(date, slot) {
        this.date = date;
        this.slot = slot;
        this.attemptNo = nextAttemptNo(date, slot);

        this._timeMs = 0;
        this._pauseCount = 0;
        this._wrong = 0;
        this._hints = 0;
        this._maxIdleMs = 0;
        this._longIdleMs = 0;
        this._done = false;

        const now = performance.now();
        this._lastTick = now;
        this._lastActivity = now;

        this._onTick = () => this._tick();
        this._onVis = () => {
            if (document.hidden) {
                this._pauseCount++;
            } else {
                // 回前台：隐藏期间不算时长也不算 idle
                this._lastTick = performance.now();
                this._lastActivity = performance.now();
            }
        };
        this._onActivity = () => this._activity();

        this._timer = setInterval(this._onTick, TICK_MS);
        document.addEventListener('visibilitychange', this._onVis);
        window.addEventListener('pointerdown', this._onActivity, true);
        window.addEventListener('keydown', this._onActivity, true);
    }

    _tick() {
        if (document.hidden || this._done) return;
        const now = performance.now();
        this._timeMs += now - this._lastTick;
        this._lastTick = now;
    }

    _activity() {
        if (document.hidden || this._done) return;
        const now = performance.now();
        const gap = now - this._lastActivity;
        if (gap > this._maxIdleMs) this._maxIdleMs = gap;
        if (gap > LONG_IDLE_MS) this._longIdleMs += gap;
        this._lastActivity = now;
    }

    /** 放置错误一次（红叉）。 */
    wrong() {
        this._wrong++;
    }

    /** 用了一次提示。 */
    hint() {
        this._hints++;
    }

    /**
     * 结束本次尝试并上报。幂等：重复调用只报一次。
     * @param {{solved: boolean, gaveUp?: boolean}} outcome
     */
    finish({ solved, gaveUp = false }) {
        if (this._done) return;
        this._activity(); // 收尾前把最后一段 idle 结算进 max_idle
        this._done = true;
        clearInterval(this._timer);
        document.removeEventListener('visibilitychange', this._onVis);
        window.removeEventListener('pointerdown', this._onActivity, true);
        window.removeEventListener('keydown', this._onActivity, true);

        send('/api/attempt', {
            player_id: playerId(),
            date: this.date,
            slot: this.slot,
            time_seconds: Math.round(this._timeMs / 1000),
            long_idle_seconds: Math.round(this._longIdleMs / 1000),
            solved: solved ? 1 : 0,
            gave_up: gaveUp ? 1 : 0,
            max_idle: Math.round(this._maxIdleMs / 1000),
            wrong_count: this._wrong,
            attempt_no: this.attemptNo,
            pause_count: this._pauseCount,
            device: deviceKind(),
            hint_count: this._hints,
            client_ts: Date.now(),
        });
    }

    /** 放弃当前尝试而不上报（如通过分享链接换题等非答题结束）。 */
    abandon() {
        if (this._done) return;
        this._done = true;
        clearInterval(this._timer);
        document.removeEventListener('visibilitychange', this._onVis);
        window.removeEventListener('pointerdown', this._onActivity, true);
        window.removeEventListener('keydown', this._onActivity, true);
    }
}
